import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Check, Search, Rocket, ExternalLink, User } from "lucide-react";

const API = '/api';

export default function Deploy() {
  const [userEmail, setUserEmail] = useState("rok.kobal@europacific.com");
  const [mode, setMode] = useState("full");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const previewRef = useRef(null);

  const renderSignature = async () => {
    setLoading(true);
    setResult(null);
    try {
      const resp = await axios.get(`${API}/deploy/render/${encodeURIComponent(userEmail)}?mode=${mode}`);
      setResult(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to render signature");
    }
    setLoading(false);
  };

  const copyHtml = () => {
    if (!result?.signature_html) return;
    navigator.clipboard.writeText(result.signature_html);
    setCopied(true);
    toast.success("Signature HTML copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyRichText = async () => {
    if (!result?.signature_html || !previewRef.current) return;
    try {
      const blob = new Blob([result.signature_html], { type: "text/html" });
      await navigator.clipboard.write([new ClipboardItem({ "text/html": blob })]);
      toast.success("Rich signature copied — paste directly into Outlook");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback to plain HTML copy
      copyHtml();
    }
  };

  return (
    <div className="p-8 max-w-[1600px]" data-testid="deploy-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Deploy Signature</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Render and deploy personalized signatures to Outlook</p>
        </div>
      </div>

      {/* Render form */}
      <div className="mt-6 bg-white border border-[#E1DFDD] rounded-sm p-5">
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-md">
            <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1.5 block">User Email</label>
            <Input
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="user@europacific.com"
              className="border-[#E1DFDD] rounded-sm"
              data-testid="deploy-email-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E] mb-1.5 block">Mode</label>
            <div className="flex gap-1">
              {["full", "short"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  data-testid={`mode-${m}-btn`}
                  className={`px-3 py-2 text-sm rounded-sm font-medium transition-colors ${
                    mode === m ? "bg-[#0F6CBD] text-white" : "bg-[#F3F2F1] text-[#5E5E5E] hover:bg-[#E1DFDD]"
                  }`}
                >
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={renderSignature}
            disabled={loading || !userEmail}
            className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm"
            data-testid="render-signature-btn"
          >
            <Rocket size={14} className="mr-2" />
            {loading ? "Rendering..." : "Render Signature"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Info + Actions */}
          <div className="lg:col-span-4 space-y-4">
            {/* User info card */}
            <div className="bg-white border border-[#E1DFDD] rounded-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#0F6CBD] flex items-center justify-center">
                  <User size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-medium text-[#171717]">{result.user_name}</p>
                  <p className="text-xs font-mono text-[#5E5E5E]">{result.user_email}</p>
                </div>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-[#5E5E5E]">Brand</span><span className="text-[#171717] font-medium">{result.brand}</span></div>
                <div className="flex justify-between"><span className="text-[#5E5E5E]">Template</span><span className="text-[#171717] font-medium">{result.template}</span></div>
                <div className="flex justify-between"><span className="text-[#5E5E5E]">Mode</span><span className={`font-semibold ${result.mode === "full" ? "text-[#0F6CBD]" : "text-[#D83B01]"}`}>{result.mode.toUpperCase()}</span></div>
              </div>
            </div>

            {/* Copy actions */}
            <div className="bg-white border border-[#E1DFDD] rounded-sm p-5 space-y-3">
              <h3 className="font-heading text-sm font-medium text-[#171717]">Deploy to Outlook</h3>

              <Button
                onClick={copyRichText}
                className="w-full bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm"
                data-testid="copy-rich-btn"
              >
                {copied ? <Check size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
                {copied ? "Copied!" : "Copy Signature (Rich)"}
              </Button>

              <Button
                onClick={copyHtml}
                variant="outline"
                className="w-full border-[#E1DFDD] rounded-sm text-xs"
                data-testid="copy-html-deploy-btn"
              >
                <Copy size={12} className="mr-2" /> Copy Raw HTML
              </Button>

              {result.signature_text && (
                <Button
                  onClick={() => { navigator.clipboard.writeText(result.signature_text); toast.success("Plain text copied"); }}
                  variant="outline"
                  className="w-full border-[#E1DFDD] rounded-sm text-xs"
                  data-testid="copy-text-deploy-btn"
                >
                  <Copy size={12} className="mr-2" /> Copy Plain Text
                </Button>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-[#EBF3FC] border border-[#0F6CBD]/20 rounded-sm p-4">
              <h3 className="font-heading text-sm font-medium text-[#171717] mb-2">How to set in Outlook</h3>
              <ol className="text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside">
                <li>Click <strong>"Copy Signature (Rich)"</strong> above</li>
                <li>Open <strong>Outlook</strong> &rarr; File &rarr; Options &rarr; Mail &rarr; Signatures</li>
                <li>Create new or edit existing signature</li>
                <li><strong>Paste</strong> (Ctrl+V) into the signature editor</li>
                <li>Set as default for new messages + replies</li>
                <li>Click OK</li>
              </ol>
              <div className="mt-3 pt-2 border-t border-[#0F6CBD]/20">
                <p className="text-[10px] text-[#5E5E5E]"><strong>Outlook Web:</strong></p>
                <a
                  href="https://outlook.office.com/mail/options/mail/messageContent"
                  target="_blank"
                  rel="noopener"
                  className="text-xs text-[#0F6CBD] hover:underline flex items-center gap-1 mt-0.5"
                >
                  Open Outlook Web Signature Settings <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-8">
            <div className="bg-white border border-[#E1DFDD] rounded-sm">
              <div className="px-5 py-3 border-b border-[#E1DFDD] flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Signature Preview</h3>
                <span className="text-[10px] text-[#5E5E5E]">{result.mode.toUpperCase()} signature</span>
              </div>
              <div className="p-6 bg-[#FAFAFA]" data-testid="deploy-preview">
                {/* Email-like container */}
                <div className="bg-white rounded-sm border border-[#E1DFDD] max-w-[650px] mx-auto">
                  <div className="px-4 py-2 border-b border-[#E1DFDD] text-xs text-[#5E5E5E]">
                    <span className="font-medium text-[#171717]">From:</span> {result.user_name} &lt;{result.user_email}&gt;
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-[#5E5E5E] mb-4 italic">[ Email body would appear here ]</p>
                    <div ref={previewRef} dangerouslySetInnerHTML={{ __html: result.signature_html }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
